import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Plus, Trash2, Upload, Calculator } from 'lucide-react';
import {
  getRebateProgram, getRebateStatement, addRebateTier, deleteRebateTier,
  importRebateUtilization, accrueRebate,
} from '../../config/api';

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => (d ? new Date(d).toISOString().slice(0, 10) : '—');

const emptyTier = { productId: '', rateType: 'PERCENT', rate: '', basis: '', minUnits: '', maxUnits: '' };

export default function RebateDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [tier, setTier] = useState(emptyTier);
  const [showTierForm, setShowTierForm] = useState(false);
  const [period, setPeriod] = useState({ periodStart: '', periodEnd: '' });
  const [file, setFile] = useState(null);
  const [banner, setBanner] = useState(null); // {type, text}

  const { data: program, isLoading } = useQuery({
    queryKey: ['rebate-program', id],
    queryFn: () => getRebateProgram(id),
  });
  const { data: statement } = useQuery({
    queryKey: ['rebate-statement', id],
    queryFn: () => getRebateStatement(id),
  });

  const refetchAll = () => {
    queryClient.invalidateQueries({ queryKey: ['rebate-program', id] });
    queryClient.invalidateQueries({ queryKey: ['rebate-statement', id] });
  };

  const addTierMut = useMutation({
    mutationFn: (payload) => addRebateTier(id, payload),
    onSuccess: () => { setTier(emptyTier); setShowTierForm(false); refetchAll(); },
    onError: (e) => setBanner({ type: 'error', text: e?.response?.data?.error || 'Failed to add tier' }),
  });
  const delTierMut = useMutation({
    mutationFn: (tierId) => deleteRebateTier(id, tierId),
    onSuccess: refetchAll,
  });
  const importMut = useMutation({
    mutationFn: (f) => importRebateUtilization(id, f),
    onSuccess: (res) => {
      const s = res?.summary || {};
      setBanner({ type: 'success', text: `Imported ${s.imported ?? 0} rows (${s.matched ?? 0} matched to products, ${s.unmatched ?? 0} unmatched).` });
      setFile(null);
      refetchAll();
    },
    onError: (e) => setBanner({ type: 'error', text: e?.response?.data?.error || 'Import failed' }),
  });
  const accrueMut = useMutation({
    mutationFn: (payload) => accrueRebate(id, payload),
    onSuccess: (res) => {
      const a = res?.data || {};
      setBanner({ type: 'success', text: `Accrued ${money(a.rebateAmount)} rebate over ${a.lineCount ?? 0} utilization lines${res?.unmatched ? ` (${res.unmatched} lines had no matching tier)` : ''}.` });
      refetchAll();
    },
    onError: (e) => setBanner({ type: 'error', text: e?.response?.data?.error || 'Accrual failed' }),
  });

  if (isLoading || !program) return <div className="p-6 text-gray-500">Loading program…</div>;

  const submitTier = (e) => {
    e.preventDefault();
    setBanner(null);
    if (tier.rate === '' || isNaN(Number(tier.rate))) { setBanner({ type: 'error', text: 'Rate must be numeric.' }); return; }
    addTierMut.mutate({
      ...tier,
      productId: tier.productId.trim() || null,
      basis: tier.basis || null,
      minUnits: tier.minUnits === '' ? null : Number(tier.minUnits),
      maxUnits: tier.maxUnits === '' ? null : Number(tier.maxUnits),
    });
  };

  const submitAccrue = (e) => {
    e.preventDefault();
    setBanner(null);
    if (!period.periodStart || !period.periodEnd) { setBanner({ type: 'error', text: 'Pick a start and end date.' }); return; }
    accrueMut.mutate(period);
  };

  const tf = (k) => (e) => setTier((t) => ({ ...t, [k]: e.target.value }));

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <button onClick={() => navigate('/app/rebates')} className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4">
        <ArrowLeft className="w-4 h-4" /> All rebate programs
      </button>

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{program.name}</h1>
        <div className="text-gray-500 mt-1">
          {program.programNumber} · {program.payerName} · {(program.payerType || '').replace(/_/g, ' ')} ·
          <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium ${program.status === 'ACTIVE' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{program.status}</span>
        </div>
        <div className="text-xs text-gray-400 mt-1">
          Term {fmtDate(program.startDate)} → {fmtDate(program.endDate)} · Default basis {program.basis} · {program._count?.utilizations ?? 0} utilization lines
        </div>
      </div>

      {banner && (
        <div className={`mb-5 text-sm rounded p-3 border ${banner.type === 'error' ? 'text-red-700 bg-red-50 border-red-200' : 'text-green-700 bg-green-50 border-green-200'}`}>
          {banner.text}
        </div>
      )}

      {/* Rate tiers */}
      <section className="bg-white rounded-lg shadow p-5 mb-6 no-card-hover">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-gray-800">Rate Tiers</h2>
          <button onClick={() => setShowTierForm((s) => !s)} className="inline-flex items-center gap-1 text-sm px-3 py-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
            <Plus className="w-4 h-4" /> Add Tier
          </button>
        </div>

        {showTierForm && (
          <form onSubmit={submitTier} className="grid grid-cols-1 md:grid-cols-6 gap-3 mb-4 items-end bg-gray-50 rounded-lg p-3">
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Product ID (blank = all)</label>
              <input value={tier.productId} onChange={tf('productId')} className="w-full px-2 py-1.5 border border-gray-300 rounded" placeholder="all products" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Rate Type</label>
              <select value={tier.rateType} onChange={tf('rateType')} className="w-full px-2 py-1.5 border border-gray-300 rounded">
                <option value="PERCENT">Percent</option>
                <option value="PER_UNIT">Per Unit</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Rate {tier.rateType === 'PERCENT' ? '(%)' : '($/unit)'}</label>
              <input type="number" step="0.0001" value={tier.rate} onChange={tf('rate')} className="w-full px-2 py-1.5 border border-gray-300 rounded" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Min Units</label>
              <input type="number" value={tier.minUnits} onChange={tf('minUnits')} className="w-full px-2 py-1.5 border border-gray-300 rounded" placeholder="0" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Max Units</label>
              <input type="number" value={tier.maxUnits} onChange={tf('maxUnits')} className="w-full px-2 py-1.5 border border-gray-300 rounded" placeholder="∞" />
            </div>
            <div className="md:col-span-6 flex gap-2">
              <button type="submit" disabled={addTierMut.isPending} className="px-3 py-1.5 bg-primary-600 text-white rounded text-sm disabled:opacity-50">Save Tier</button>
              <button type="button" onClick={() => { setShowTierForm(false); setTier(emptyTier); }} className="px-3 py-1.5 border border-gray-300 rounded text-sm">Cancel</button>
            </div>
          </form>
        )}

        {(!program.tiers || program.tiers.length === 0) ? (
          <div className="text-sm text-gray-500">No tiers yet. Add at least one so accruals can compute.</div>
        ) : (
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 uppercase">
                <th className="py-2 pr-4">Product</th>
                <th className="py-2 pr-4">Type</th>
                <th className="py-2 pr-4 text-right">Rate</th>
                <th className="py-2 pr-4 text-right">Volume Band</th>
                <th className="py-2 pr-4"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {program.tiers.map((t) => (
                <tr key={t.id}>
                  <td className="py-2 pr-4">{t.productId ? <span className="font-mono text-xs">{t.productId.slice(0, 8)}…</span> : <span className="text-gray-500">All products</span>}</td>
                  <td className="py-2 pr-4">{t.rateType === 'PERCENT' ? 'Percent' : 'Per unit'}</td>
                  <td className="py-2 pr-4 text-right">{t.rateType === 'PERCENT' ? `${Number(t.rate)}%` : money(t.rate)}</td>
                  <td className="py-2 pr-4 text-right">{Number(t.minUnits || 0).toLocaleString()} – {t.maxUnits != null ? Number(t.maxUnits).toLocaleString() : '∞'}</td>
                  <td className="py-2 pr-4 text-right">
                    <button onClick={() => delTierMut.mutate(t.id)} className="text-red-500 hover:text-red-700" title="Delete tier"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* Utilization import */}
      <section className="bg-white rounded-lg shadow p-5 mb-6 no-card-hover">
        <h2 className="font-semibold text-gray-800 mb-2">Payer Utilization Import</h2>
        <p className="text-sm text-gray-500 mb-3">CSV columns: <code className="text-xs bg-gray-100 px-1 rounded">product_ndc, product_name, period_start, period_end, units, gross_sales</code>. Percent tiers rebate on <em>gross_sales</em>; per-unit tiers on <em>units</em>.</p>
        <div className="flex items-center gap-3">
          <input type="file" accept=".csv" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm" />
          <button disabled={!file || importMut.isPending} onClick={() => file && importMut.mutate(file)} className="inline-flex items-center gap-1 px-3 py-1.5 bg-primary-600 text-white rounded-lg text-sm disabled:opacity-50">
            <Upload className="w-4 h-4" /> {importMut.isPending ? 'Importing…' : 'Import'}
          </button>
        </div>
      </section>

      {/* Accrue + statement */}
      <section className="bg-white rounded-lg shadow p-5 no-card-hover">
        <h2 className="font-semibold text-gray-800 mb-4">Rebate Statement</h2>
        <form onSubmit={submitAccrue} className="flex flex-wrap items-end gap-3 mb-5">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">From</label>
            <input type="date" value={period.periodStart} onChange={(e) => setPeriod((p) => ({ ...p, periodStart: e.target.value }))} className="px-3 py-2 border border-gray-300 rounded-lg" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">To</label>
            <input type="date" value={period.periodEnd} onChange={(e) => setPeriod((p) => ({ ...p, periodEnd: e.target.value }))} className="px-3 py-2 border border-gray-300 rounded-lg" />
          </div>
          <button type="submit" disabled={accrueMut.isPending} className="inline-flex items-center gap-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50">
            <Calculator className="w-4 h-4" /> {accrueMut.isPending ? 'Accruing…' : 'Accrue Period'}
          </button>
        </form>

        {statement?.accruals?.length > 0 ? (
          <>
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 uppercase">
                  <th className="py-2 pr-4">Period</th>
                  <th className="py-2 pr-4 text-right">Eligible Units</th>
                  <th className="py-2 pr-4 text-right">Eligible Sales</th>
                  <th className="py-2 pr-4 text-right">Rebate</th>
                  <th className="py-2 pr-4 text-right">Lines</th>
                  <th className="py-2 pr-4">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {statement.accruals.map((a) => (
                  <tr key={a.id}>
                    <td className="py-2 pr-4">{fmtDate(a.periodStart)} → {fmtDate(a.periodEnd)}</td>
                    <td className="py-2 pr-4 text-right">{Number(a.eligibleUnits || 0).toLocaleString()}</td>
                    <td className="py-2 pr-4 text-right">{money(a.eligibleSales)}</td>
                    <td className="py-2 pr-4 text-right font-medium text-gray-900">{money(a.rebateAmount)}</td>
                    <td className="py-2 pr-4 text-right">{a.lineCount}</td>
                    <td className="py-2 pr-4"><span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600">{a.status}</span></td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-gray-200 font-semibold text-gray-900">
                  <td className="py-2 pr-4">Total rebate</td>
                  <td></td><td></td>
                  <td className="py-2 pr-4 text-right">{money(statement.totalRebate)}</td>
                  <td></td><td></td>
                </tr>
              </tfoot>
            </table>
          </>
        ) : (
          <div className="text-sm text-gray-500">No accruals yet. Import utilization, add tiers, then accrue a period.</div>
        )}
      </section>
    </div>
  );
}
